import React, { useMemo, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useAtom } from 'jotai'
import {
  DatePicker,
  FormElement,
  GridCell,
  Select,
} from 'mailup-web-components'
import { filterFormImmerAtom } from '../../state/state'
import { type ConditionProps } from '../../types'
import { type SelectOption } from '../../../../helpers/types'
import { TrackingMatch } from '../../../../types/FilterCondition'
import {
  type TrackingDateRangeValue,
  type UiTrackingSpec as UiTrackingSpecType,
} from '../../filterConditionTypes'
import { datePickerDateFormat } from '../../../../config/config'

const emptyDates = (): NonNullable<UiTrackingSpecType['Dates']> => ({
  Type: 'DateRangeDetails',
  Details: { From: null, To: null },
})

const TrackingCondition: FC<ConditionProps> = ({ blockIndex, condIndex }) => {
  const intl = useIntl()
  const [filter, setFilter] = useAtom(filterFormImmerAtom)
  const condition = filter.Blocks[blockIndex].Conditions[condIndex]
  const spec = condition.Spec as UiTrackingSpecType

  const fieldsOptions: SelectOption[] = useMemo(
    () => [
      {
        label: intl.formatMessage({
          id: 'Load.Filters.ActivityDateDetailsAny',
        }),
        value: TrackingMatch.TrackingAnytime,
      },
      {
        label: intl.formatMessage({
          id: 'Load.Filters.ActivityDateDetailsRange',
        }),
        value: TrackingMatch.TrackingDate,
      },
    ],
    [intl],
  )

  const handleChange = (value: TrackingMatch): void => {
    setFilter((draft) => {
      const nextSpec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as UiTrackingSpecType
      nextSpec.TrackingMatchOperator = value
      nextSpec.Dates = emptyDates()
    })
  }

  const handleDateChange = (
    field: keyof TrackingDateRangeValue,
    value: Date,
  ): void => {
    setFilter((draft) => {
      const nextSpec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as UiTrackingSpecType
      const dates = nextSpec.Dates ?? emptyDates()
      dates.Details[field] = value
      nextSpec.Dates = dates
    })
  }

  const dateRange = spec.Dates?.Details
  const isDateRange = spec.TrackingMatchOperator === TrackingMatch.TrackingDate

  return (
    <>
      <GridCell span={3}>
        <FormElement>
          <Select
            options={fieldsOptions}
            value={spec.TrackingMatchOperator}
            onChange={handleChange}
          />
        </FormElement>
      </GridCell>
      {isDateRange && (
        <>
          <GridCell span={3}>
            <FormElement>
              <DatePicker
                value={dateRange?.From ?? null}
                callback={(value: Date) => {
                  handleDateChange('From', value)
                }}
                locale={intl.locale}
                dateFormat={datePickerDateFormat}
                isClearable={false}
                maxDate={dateRange?.To ?? null}
                placeholder={intl.formatMessage({
                  id: 'Load.MailupCommon.FromDate',
                })}
              />
            </FormElement>
          </GridCell>
          <GridCell span={3}>
            <FormElement>
              <DatePicker
                value={dateRange?.To ?? null}
                callback={(value: Date) => {
                  handleDateChange('To', value)
                }}
                locale={intl.locale}
                dateFormat={datePickerDateFormat}
                isClearable={false}
                minDate={dateRange?.From ?? null}
                placeholder={intl.formatMessage({
                  id: 'Load.MailupCommon.ToDate',
                })}
              />
            </FormElement>
          </GridCell>
        </>
      )}
    </>
  )
}

export default TrackingCondition
